import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Navigate } from 'react-router-dom';
import { lazy } from 'react';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import MyDay from './pages/MyDay';

/* Every page but My Day and the Dashboard is its own file, fetched the first
   time someone opens it, so the first visit downloads those two and not all
   eighteen - nor the PDF builder and the chat formatter that only some pages
   use. My Day stays in the first download because it is where everyone lands,
   and the Dashboard because it is the next click from there and reads the
   same lists. Layout holds the Suspense boundary that shows while a page
   arrives, and lib/chunkReload.js recovers a tab opened before a deploy. */
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const PlanReview = lazy(() => import('./pages/PlanReview'));
const ImportProjects = lazy(() => import('./pages/ImportProjects'));
const ArchivedProjects = lazy(() => import('./pages/ArchivedProjects'));
const Suggestions = lazy(() => import('./pages/Suggestions'));
const Reports = lazy(() => import('./pages/Reports'));
const SystemTest = lazy(() => import('./pages/SystemTest'));
const TaskUpdates = lazy(() => import('./pages/TaskUpdates'));
const BulkUpdates = lazy(() => import('./pages/BulkUpdates'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const PropertyManagement = lazy(() => import('./pages/PropertyManagement'));
const DocumentTypes = lazy(() => import('./pages/DocumentTypes'));
const LienWaiverForms = lazy(() => import('./pages/LienWaiverForms'));
const Contractors = lazy(() => import('./pages/Contractors'));
const OpenDocument = lazy(() => import('./pages/OpenDocument'));

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/MyDay" replace />} />
      <Route element={<Layout />}>
        <Route path="/MyDay" element={<MyDay />} />
        <Route path="/Dashboard" element={<Dashboard />} />
        <Route path="/Projects" element={<Projects />} />
        <Route path="/ProjectDetail" element={<ProjectDetail />} />
        <Route path="/PlanReview" element={<PlanReview />} />
        <Route path="/ImportProjects" element={<ImportProjects />} />
        <Route path="/ArchivedProjects" element={<ArchivedProjects />} />
        <Route path="/Reports" element={<Reports />} />
        <Route path="/SystemTest" element={<SystemTest />} />
        <Route path="/Suggestions" element={<Suggestions />} />
        <Route path="/TaskUpdates" element={<TaskUpdates />} />
        <Route path="/BulkUpdates" element={<BulkUpdates />} />
        <Route path="/UserManagement" element={<UserManagement />} />
        <Route path="/PropertyManagement" element={<PropertyManagement />} />
        <Route path="/DocumentTypes" element={<DocumentTypes />} />
        <Route path="/LienWaiverForms" element={<LienWaiverForms />} />
        <Route path="/Contractors" element={<Contractors />} />
        <Route path="/OpenDocument" element={<OpenDocument />} />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  /* The last resort, outside every provider: a crash in the layout, the
     router or auth itself lands here rather than on a blank page. Ordinary
     page crashes are caught closer in, around the outlet in Layout. */
  return (
    <ErrorBoundary variant="page">
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <AuthenticatedApp />
          </Router>
          <Toaster />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App